import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    EffectCleanupRegisterFn,
    ElementRef,
    HostListener,
    inject,
    input,
    linkedSignal,
    NgZone,
    Renderer2,
    RendererStyleFlags2,
    Signal,
    signal,
    TemplateRef,
    untracked,
    viewChild,
    viewChildren,
    ViewEncapsulation,
} from "@angular/core";
import { AriaLive } from "../../services/aria.live.service";
import { GridUtil } from "./s25.virtual.grid.util";
import { Grid } from "./s25.virtual.grid";
import { S25Util } from "../../util/s25-util";
import { Bind } from "../../decorators/bind.decorator";
import { Proto } from "../../pojo/Proto";
import { S25ScrollUtil } from "../../standalone/s25-scroll-minimap/s25.scroll.util";
import { S25ModalComponent } from "../s25-modal/s25.modal.component";
import { History } from "../../util/history.util";
import { S25VirtualGridDirective } from "./s25.virtual.grid.directive";
import { GridDrag } from "./s25.grid.drag";
import { S25Const } from "../../util/s25-const";
import { Nullable } from "../../pojo/Util";

type RenderedDecorationData<
    ItemType extends Grid.SourceItem,
    ProjectionData extends Grid.CustomData,
    DecorationData extends Grid.CustomData,
> = DecorationData | Grid.OverlapOverlayData<ItemType, ProjectionData>;

type GridRenderEntry<
    ItemType extends Grid.SourceItem,
    ProjectionData extends Grid.CustomData,
    DecorationData extends Grid.CustomData,
> = Grid.RenderEntry<ItemType, ProjectionData, RenderedDecorationData<ItemType, ProjectionData, DecorationData>>;

@Component({
    selector: "s25-ng-virtual-grid",
    template: `
        @if (optionsLeftTemplate() || optionsMiddleTemplate() || optionsRightTemplate() || hasUndo()) {
            <div class="options">
                <div class="left">
                    <ng-container
                        [ngTemplateOutlet]="coalesceOptionsTmpl()"
                        [ngTemplateOutletContext]="{ options: optionsLeftTemplate(), defaults: optionsLeft }"
                    />
                </div>
                <div class="middle">
                    <ng-container
                        [ngTemplateOutlet]="coalesceOptionsTmpl()"
                        [ngTemplateOutletContext]="{ options: optionsMiddleTemplate(), defaults: optionsMiddle }"
                    />
                </div>
                <div class="right">
                    <ng-container
                        [ngTemplateOutlet]="coalesceOptionsTmpl()"
                        [ngTemplateOutletContext]="{ options: optionsRightTemplate(), defaults: optionsRight }"
                    />
                </div>
            </div>
        }
        <ng-container [ngTemplateOutlet]="optionsBelowTemplate()" />
        @if (!!displayMessage()) {
            <p class="message">{{ displayMessage() }}</p>
        } @else {
            <div
                class="grid"
                [class.resizing]="!!resizeTimeout()"
                [style.--column-depth]="columnHeadersMeta.depth()"
                [style.--row-depth]="rowHeadersMeta.depth()"
                [style.--column-count]="columnHeadersMeta.visibleCount()"
                [style.--row-count]="rowHeadersMeta.visibleCount()"
                [style.--frozen-row-count]="frozenRows()"
            >
                <!-- Row headers -->
                <div #rowHeadersElem class="grid--row-headers">
                    <div class="grid--corner">
                        <ng-container [ngTemplateOutlet]="cornerTemplate()" />
                    </div>
                    <ng-container
                        [ngTemplateOutlet]="headersTmpl()"
                        [ngTemplateOutletContext]="{
                            headers: rowHeaders(),
                            type: 'row',
                            template: rowHeaderTemplate(),
                            frozenHeaders: frozenRows(),
                        }"
                    />
                </div>
                <!-- Row header resizer -->
                <div class="grid--row-headers--resize">
                    <div
                        class="grid--row-headers--resize-handle"
                        (mousedown)="onRowHeaderResizeMousedown($event)"
                    ></div>
                </div>
                <!-- Scrolling area -->
                <div #scrollAreaElement class="grid--scroll-area">
                    <!-- Full width area -->
                    <div class="grid--scroll-area-inner">
                        <!-- Headers -->
                        <div class="grid--column-headers">
                            <ng-container
                                [ngTemplateOutlet]="headersTmpl()"
                                [ngTemplateOutletContext]="{
                                    headers: columnHeaders(),
                                    type: 'column',
                                    template: columnHeaderTemplate(),
                                }"
                            />
                        </div>
                        <!-- Grid area -->
                        <div #gridAreaElement class="grid--area" [attr.dragging-type]="dragSession()?.type">
                            <!-- Row stripes -->
                            <div class="grid--row-stripes">
                                @for (row of rowHeadersMeta.flatMeta(); track row.header.id) {
                                    @if (!row.hidden) {
                                        <div class="grid--row-stripe"></div>
                                    }
                                }
                            </div>
                            <!-- Column lines -->
                            <ng-container [ngTemplateOutlet]="columnLinesTemplate" />
                            <!-- Create Element -->
                            @if (canCreate() && createTemplate()) {
                                <div #createItem class="grid--create-item" (mousedown)="onCreateMousedown($event)">
                                    <ng-container [ngTemplateOutlet]="createTemplate()" />
                                </div>
                            }
                            <!-- Render entries -->
                            @for (layer of renderLayers; track layer.name) {
                                <div
                                    class="grid--layer"
                                    [class.grid--underlays]="layer.name === 'underlay'"
                                    [class.grid--items]="layer.name === 'item'"
                                    [class.grid--overlays]="layer.name === 'overlay'"
                                    [attr.data-layer]="layer.name"
                                >
                                    <s25-ng-virtual-grid-item
                                        *virtualGrid="
                                            let entry of layer.entries();
                                            rows: rowHeadersMeta.visibleCount();
                                            columns: columnHeadersMeta.visibleCount();
                                            scrollElementX: scrollAreaElement;
                                            scrollElementY: document.documentElement;
                                            windowElement: scrollAreaElement
                                        "
                                        #gridItem
                                        [entry]="entry"
                                    />
                                </div>
                            }
                        </div>
                        <!-- Fixed only after the grid scrolls, so horizontal scrolling stays native until then. -->
                        <div #fixedRegion [style.display]="'none'" class="grid--fixed">
                            <div class="grid--fixed-inner">
                                <div class="grid--column-headers fixed">
                                    <ng-container
                                        [ngTemplateOutlet]="headersTmpl()"
                                        [ngTemplateOutletContext]="{
                                            headers: columnHeaders(),
                                            type: 'column',
                                            template: columnHeaderTemplate(),
                                        }"
                                    />
                                </div>
                                @if (showFixedHeaders() && frozenRows() > 0) {
                                    <div class="grid--frozen-rows">
                                        <div #frozenRowsPane class="grid--frozen-rows-canvas">
                                            <div class="grid--row-stripes">
                                                @for (row of frozenStripeRows(); track row.header.id) {
                                                    <div class="grid--row-stripe"></div>
                                                }
                                            </div>
                                            <ng-container [ngTemplateOutlet]="columnLinesTemplate" />
                                            @for (layer of frozenRenderLayers; track layer.name) {
                                                <div
                                                    class="grid--layer"
                                                    [class.grid--underlays]="layer.name === 'underlay'"
                                                    [class.grid--items]="layer.name === 'item'"
                                                    [class.grid--overlays]="layer.name === 'overlay'"
                                                    [attr.data-layer]="layer.name"
                                                >
                                                    <s25-ng-virtual-grid-item
                                                        *virtualGrid="
                                                            let entry of layer.entries();
                                                            rows: frozenRows();
                                                            columns: columnHeadersMeta.visibleCount();
                                                            scrollElementX: fixedRegion;
                                                            scrollElementY: document.documentElement;
                                                            windowElement: fixedRegion;
                                                            fullY: true
                                                        "
                                                        #gridItem
                                                        [entry]="entry"
                                                    />
                                                </div>
                                            }
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Minimap -->
                @if (hasMinimap()) {
                    <s25-ng-scroll-minimap
                        [scrollElementX]="scrollAreaElement"
                        [scrollElementY]="document.documentElement"
                        [windowElement]="scrollAreaElement"
                    >
                        <canvas #minimapCanvas></canvas>
                    </s25-ng-scroll-minimap>
                }
            </div>
        }

        <ng-template #columnLinesTemplate void>
            <div class="grid--column-lines">
                @for (col of columnLines(); track col.meta.header.id) {
                    <div
                        class="grid--column-line"
                        [attr.data-truncate-boundary]="col.truncateBoundary || undefined"
                    ></div>
                }
            </div>
        </ng-template>

        <ng-template #coalesceOptions [template]="coalesceOptionsTmpl()" let-options="options" let-defaults="defaults">
            <ng-container [ngTemplateOutlet]="options" [ngTemplateOutletContext]="{ defaultOptions: defaults }" />
            @if (!options) {
                <ng-container [ngTemplateOutlet]="defaults" />
            }
        </ng-template>

        <ng-template #optionsLeft></ng-template>

        <ng-template #optionsMiddle></ng-template>

        <ng-template #optionsRight>
            @if (hasUndo()) {
                <s25-ng-button [disabled]="!history.past.length" [type]="'outline'" (clicked)="undo()">
                    Undo
                </s25-ng-button>
                <s25-ng-button [disabled]="!history.future.length" [type]="'outline'" (clicked)="redo()">
                    Redo
                </s25-ng-button>
            }
        </ng-template>

        <ng-template
            #headersTemplate
            [template]="headersTmpl()"
            let-headers="headers"
            let-type="type"
            let-template="template"
            let-frozenHeaders="frozenHeaders"
        >
            @for (header of headers; track header.id) {
                @if (!header.hidden?.()) {
                    <div
                        #headerItem
                        class="grid--{{ type }}-header"
                        [class.leaf]="!header.subHeaders"
                        [class.last]="header._virtualGrid.isLast()"
                        [class.frozen]="frozenHeaders > $index"
                        [style.--frozen-index]="frozenHeaders > $index ? $index : undefined"
                        [class.lastFrozen]="frozenHeaders - 1 === $index"
                    >
                        <div class="grid--{{ type }}-header-label">
                            @if (!template) {
                                <span>{{ header.heading }}</span>
                            } @else {
                                <ng-container
                                    [ngTemplateOutlet]="template"
                                    [ngTemplateOutletContext]="{ header: header }"
                                />
                            }
                        </div>
                        @if (header.subHeaders) {
                            <div class="grid--{{ type }}-headers--sub-headers">
                                <ng-container
                                    [ngTemplateOutlet]="headersTmpl()"
                                    [ngTemplateOutletContext]="{
                                        headers: header.subHeaders,
                                        type: type,
                                        template: template,
                                    }"
                                />
                            </div>
                        }
                    </div>
                }
            }
        </ng-template>

        <s25-ng-modal #confirmationModal [type]="'confirm'" [title]="'Confirm'">{{ confirmationText() }}</s25-ng-modal>

        @if (isLoading()) {
            <div class="loading-overlay">
                <s25-ng-loading-inline-static />
            </div>
        }
    `,
    styleUrls: ["./s25.virtual.grid.component.scss", "./s25.virtual.grid.overrides.scss"],
    host: {
        "[attr.inert]": "isLoading() || undefined",
        "[class.loading]": "isLoading()",
        "[class.showFixedHeaders]": "showFixedHeaders()",
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class S25VirtualGridComponent<
    ColumnType extends Grid.SourceHeader = Grid.SourceHeader,
    RowType extends Grid.SourceHeader = Grid.SourceHeader,
    ItemType extends Grid.SourceItem = Grid.SourceItem,
    ProjectionData extends Grid.CustomData = Grid.CustomData,
    DecorationData extends Grid.CustomData = Grid.CustomData,
> {
    readonly itemsInput = input.required<readonly ItemType[]>({ alias: "items" });
    readonly columnHeadersInput = input.required<readonly ColumnType[]>({ alias: "columnHeaders" });
    readonly rowHeadersInput = input.required<readonly RowType[]>({ alias: "rowHeaders" });
    readonly frozenRows = input(0);
    readonly displayMessage = input<string>();
    readonly interactionHandlers = input<Grid.InteractionHandlers<ItemType>>({});
    readonly domainEquals = input.required<Grid.DomainEquals<Grid.PositionOf<ItemType>>>();
    // Template for blank corner in the top left
    readonly cornerTemplate = input<TemplateRef<void>>();
    readonly columnHeaderTemplate = input<TemplateRef<Grid.HeaderTemplateContext<ColumnType>>>();
    readonly rowHeaderTemplate = input<TemplateRef<Grid.HeaderTemplateContext<RowType>>>();
    readonly itemProjectionTemplate = input<TemplateRef<Grid.ItemProjectionTemplateContext<ItemType, ProjectionData>>>(
        undefined,
        { alias: "itemTemplate" },
    );
    readonly decorationTemplate =
        input<TemplateRef<Grid.DecorationTemplateContext<ItemType, ProjectionData, DecorationData>>>();
    readonly itemProjector = input.required<Grid.ItemProjector<ItemType, ProjectionData>>();
    readonly supplementalDecorations = input<readonly Grid.SourceDecoration<DecorationData>[]>([]);
    readonly itemInitialized = input<(item: Grid.Item<ItemType>) => void>();
    readonly createTemplate = input<TemplateRef<void>>();
    // Template for left part of options bar
    readonly optionsLeftTemplate = input<TemplateRef<Grid.OptionsTemplateContext>>();
    // Template for middle part of options bar
    readonly optionsMiddleTemplate = input<TemplateRef<Grid.OptionsTemplateContext>>();
    // Template for right part of options bar
    readonly optionsRightTemplate = input<TemplateRef<Grid.OptionsTemplateContext>>();
    // Template for below the options bar
    readonly optionsBelowTemplate = input<TemplateRef<void>>();
    readonly canDragInput = input<{ x: boolean; y: boolean }>(undefined, { alias: "canDrag" });
    readonly hasMinimap = input(false);
    readonly hasUndo = input(false);
    readonly canCreate = input<{ x: boolean; y: boolean }>();
    // Fraction of a header width
    readonly snapStep = input(S25VirtualGridComponent.DEFAULT_SNAP_STEP, {
        transform: (step: Nullable<Grid.Coord>): Grid.Coord => step ?? S25VirtualGridComponent.DEFAULT_SNAP_STEP,
    });
    readonly dragParticipantsResolver = input<Grid.DragParticipantsResolver<ItemType>>();
    // If true, grid will add a list of overlapping items to each item
    readonly findOverlap = input(false);
    // If true, will create overlay decorations representing sets of overlapping items
    readonly generateOverlapProjections = input(false);
    readonly isLoading = input(false);

    // Template views
    private readonly scrollArea = viewChild("scrollAreaElement", { read: ElementRef });
    private readonly gridArea = viewChild("gridAreaElement", { read: ElementRef });
    private readonly fixedRegion = viewChild("fixedRegion", { read: ElementRef });
    private readonly frozenRowsPane = viewChild("frozenRowsPane", { read: ElementRef });
    private readonly rowHeaderElems = viewChild("rowHeadersElem", { read: ElementRef });
    private readonly createItem = viewChild("createItem", { read: ElementRef });
    private readonly confirmationModal = viewChild("confirmationModal", { read: S25ModalComponent });
    readonly gridDirectives =
        viewChildren<S25VirtualGridDirective<GridRenderEntry<ItemType, ProjectionData, DecorationData>>>(
            S25VirtualGridDirective,
        );
    private readonly minimapCanvas = viewChild<string, ElementRef<HTMLCanvasElement>>("minimapCanvas", {
        read: ElementRef,
    });
    protected readonly coalesceOptionsTmpl = viewChild<
        string,
        TemplateRef<{ options: TemplateRef<Grid.OptionsTemplateContext>; defaults: TemplateRef<void> }>
    >("coalesceOptions", { read: TemplateRef });
    protected readonly headersTmpl = viewChild<string, TemplateRef<Grid.HeadersTemplateContext<ColumnType | RowType>>>(
        "headersTemplate",
        { read: TemplateRef },
    );

    public static AUTO_SCROLL_THRESHOLD = 0.15; // Fraction of window
    public static AUTO_SCROLL_STEP = 5; // Pixels per scroll
    public static ITEM_MOVE_DURATION = 400; // Milliseconds an item takes to slide to a new position
    private static readonly DEFAULT_SNAP_STEP = { x: 1, y: 1 };

    protected readonly document = document;
    private readonly elementRef = inject(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly zone = inject(NgZone);

    constructor() {
        effect(this.setEventListeners);
        effect(this.drawMinimap);
        effect(this.checkForDuplicateIds);
        effect(this.checkForDuplicateRenderKeys);
    }

    private readonly blockDragging = signal(false);
    private readonly canDrag = computed(() => (this.blockDragging() ? undefined : this.canDragInput()));

    protected readonly resizeTimeout = signal<NodeJS.Timeout | undefined>(undefined);
    protected readonly showFixedHeaders = signal(false);
    public readonly dragSession = signal<Grid.DragSession<ItemType, ProjectionData> | undefined>(undefined);
    private dragFrame: number | undefined;
    private readonly resizingRowHeaders = signal<{ initX: number; initWidth: number } | undefined>(undefined);
    protected readonly confirmationText = signal<string | undefined>(undefined);
    readonly history: History<ReadonlyMap<Grid.SourceItem["id"], Grid.PositionOf<ItemType>>, Grid.PositionHistory> =
        new History();
    private readonly creating = signal<Grid.Creating | undefined>(undefined);
    private readonly isUnOrRedoing = signal(false);
    protected readonly columnHeadersMeta = GridUtil.getHeadersMetadata(this.columnHeadersInput);
    protected readonly rowHeadersMeta = GridUtil.getHeadersMetadata(this.rowHeadersInput);

    private readonly items = computed((): Grid.Item<ItemType>[] => {
        if (!this.columnHeadersMeta.flatMeta() || !this.rowHeadersMeta.flatMeta()) return [];
        const uninitializedItems = this.itemsInput();
        return untracked(() => uninitializedItems.map((item) => this.initializeItem(item)));
    });
    private readonly itemById = computed(() => {
        const items = this.items();
        return new Map(items.map((item) => [item.id, item]));
    });

    protected columnHeaders = computed(() => {
        if (!this.columnHeadersMeta.flatMeta()) return [];
        const headers = this.columnHeadersInput();
        return untracked(() => headers.map((header) => GridUtil.initializeHeader(header, this.columnHeadersMeta)));
    });
    protected rowHeaders = computed(() => {
        if (!this.rowHeadersMeta.flatMeta()) return [];
        const headers = this.rowHeadersInput();
        return untracked(() => headers.map((header) => GridUtil.initializeHeader(header, this.rowHeadersMeta)));
    });

    private readonly overlapSource = computed<Grid.OverlapSource<ItemType, ProjectionData>>(() => {
        if (!this.findOverlap()) return "disabled";
        if (this.dragSession()) return "frozen";
        const projections = this.visibleItemProjections();
        return {
            projections,
            columnCount: this.columnHeadersMeta.count(),
            rowCount: this.rowHeadersMeta.count(),
            positions: projections.map((projection) => projection.clippedPosition()),
        };
    });
    private readonly overlap = linkedSignal<
        Grid.OverlapSource<ItemType, ProjectionData>,
        Grid.OverlapBundle<ItemType, ProjectionData>
    >({
        source: () => this.overlapSource(),
        computation: (source, previous) => {
            if (source === "disabled") return GridUtil.emptyOverlapBundle();
            if (source === "frozen" && previous) return previous.value;
            // The source already declares the dependencies; reading them again here would re-subscribe
            // the bundle to live candidate positions and undo the freeze.
            return untracked(() => this.computeOverlap());
        },
    });
    private readonly overlapDecorations = computed<
        Grid.Decoration<Grid.OverlapOverlayData<ItemType, ProjectionData>>[]
    >(() => {
        if (!this.findOverlap() || !this.generateOverlapProjections()) return [];
        return this.overlap().segments.map((overlap) => {
            const projectionKeys = overlap.projections.map((projection) => projection.key).sort();
            const clippedPosition = GridUtil.clipPosition(
                overlap.position,
                this.columnHeadersMeta,
                this.rowHeadersMeta,
            );
            const itemWindow = GridUtil.getItemWindow(
                overlap.position,
                clippedPosition,
                this.columnHeadersMeta,
                this.rowHeadersMeta,
            );
            return {
                type: "decoration" as const,
                layer: "overlay" as const,
                key: `overlap-overlay-${JSON.stringify(projectionKeys)}`,
                data: { type: "aggregate-overlap" as const, items: overlap.items, projections: overlap.projections },
                position: signal(overlap.position).asReadonly(),
                clippedPosition: signal(clippedPosition).asReadonly(),
                window: signal(itemWindow).asReadonly(),
                renderable: signal(GridUtil.isOnGrid(overlap.position)).asReadonly(),
                dragging: signal(false).asReadonly(),
                moving: signal(false).asReadonly(),
                broughtToFront: signal(false).asReadonly(),
                hidden: signal(false).asReadonly(),
                noInteraction: signal(true).asReadonly(),
                ariaLabel: signal("Overlapping Items Indicator").asReadonly(),
                minimapColor: signal(undefined).asReadonly(),
                frozenPosition: signal(
                    GridUtil.clipToFrozenRows(clippedPosition, this.frozenRows(), this.rowHeadersMeta.visibleCount()),
                ).asReadonly(),
            };
        });
    });

    private readonly supplementalDecorationsClipped = computed<Grid.Decoration<DecorationData>[]>(() => {
        return this.supplementalDecorations().map((decoration) => {
            const clippedPosition = computed(() =>
                GridUtil.clipPosition(decoration.position(), this.columnHeadersMeta, this.rowHeadersMeta),
            );
            const itemWindow = computed(() =>
                GridUtil.getItemWindow(
                    decoration.position(),
                    clippedPosition(),
                    this.columnHeadersMeta,
                    this.rowHeadersMeta,
                ),
            );
            return {
                ...decoration,
                clippedPosition,
                window: itemWindow,
                renderable: computed(() => GridUtil.isOnGrid(clippedPosition())),
                frozenPosition: computed(() =>
                    GridUtil.clipToFrozenRows(clippedPosition(), this.frozenRows(), this.rowHeadersMeta.visibleCount()),
                ),
            };
        });
    });
    readonly underlayDecorations = computed(() =>
        this.supplementalDecorationsClipped().filter((decoration) => decoration.layer === "underlay"),
    );
    private readonly idleProjections = computed(
        () => {
            const projector = this.itemProjector();
            const items = this.items();
            const session = this.dragSession();
            if (!session) return this.projectItems(items, projector);
            const participantIds = new Set(session.participants.map((participant) => participant.id));
            const idleItems = items.filter((item) => !participantIds.has(item.id));
            return untracked(() => this.projectItems(idleItems, projector));
        },
        { equal: S25VirtualGridComponent.projectionsEqual },
    );
    private readonly draggedProjections = computed(
        () => {
            const session = this.dragSession();
            if (!session) return [];
            const projector = this.itemProjector();
            return this.projectItems(session.participants, projector);
        },
        { equal: S25VirtualGridComponent.projectionsEqual },
    );
    readonly itemProjections = computed(
        () => {
            const idle = this.idleProjections();
            const dragged = this.draggedProjections();
            if (!dragged.length) return idle;
            const byItemId = new Map<Grid.SourceItem["id"], Grid.ItemProjection<ItemType, ProjectionData>[]>();
            for (const projection of [...idle, ...dragged]) {
                const group = byItemId.get(projection.source.id);
                if (group) group.push(projection);
                else byItemId.set(projection.source.id, [projection]);
            }
            return this.items().flatMap((item) => byItemId.get(item.id) ?? []);
        },
        { equal: S25VirtualGridComponent.projectionsEqual },
    );
    private readonly supplementalOverlayDecorations = computed(() =>
        this.supplementalDecorationsClipped().filter((decoration) => decoration.layer === "overlay"),
    );
    readonly overlayDecorations = computed(() => [
        ...this.supplementalOverlayDecorations(),
        ...this.overlapDecorations(),
    ]);

    readonly renderEntries = computed(() => {
        return [...this.underlayDecorations(), ...this.itemProjections(), ...this.overlayDecorations()];
    });
    private readonly renderEntryByKey = computed(() => {
        return new Map(this.renderEntries().map((entry) => [entry.key, entry]));
    });

    protected readonly visibleItemProjections = computed(() => this.visibleSorted(this.itemProjections()));
    protected readonly visibleUnderlayDecorations = computed(() => this.visibleSorted(this.underlayDecorations()));
    protected readonly visibleOverlayDecorations = computed(() => this.visibleSorted(this.overlayDecorations()));
    // Overlap overlays are excluded: includeOnMinimap drops them, and computing them is expensive.
    readonly minimapRenderEntries = computed(() =>
        [
            ...this.visibleUnderlayDecorations(),
            ...this.visibleItemProjections(),
            ...this.visibleSorted(this.supplementalOverlayDecorations()),
        ].filter(GridUtil.includeOnMinimap),
    );
    protected readonly renderLayers: readonly {
        name: Grid.DecorationLayer | "item";
        entries: Signal<readonly GridRenderEntry<ItemType, ProjectionData, DecorationData>[]>;
    }[] = [
        { name: "underlay", entries: this.visibleUnderlayDecorations },
        { name: "item", entries: this.visibleItemProjections },
        { name: "overlay", entries: this.visibleOverlayDecorations },
    ];
    protected readonly frozenStripeRows = computed(() => this.rowHeadersMeta.flatMeta().slice(0, this.frozenRows()));
    protected readonly frozenRenderLayers: readonly {
        name: Grid.DecorationLayer | "item";
        entries: Signal<readonly GridRenderEntry<ItemType, ProjectionData, DecorationData>[]>;
    }[] = [
        { name: "underlay", entries: computed(() => this.frozenLayerEntries(this.visibleUnderlayDecorations())) },
        { name: "item", entries: computed(() => this.frozenLayerEntries(this.visibleItemProjections())) },
        { name: "overlay", entries: computed(() => this.frozenLayerEntries(this.visibleOverlayDecorations())) },
    ];

    protected readonly columnLines = computed(() => {
        return this.columnHeadersMeta
            .flatMeta()
            .filter((col) => !col.hidden)
            .map((col, i, arr) => {
                const next = arr[i + 1];
                return {
                    meta: col,
                    truncateBoundary: col.truncateRef !== next?.truncateRef,
                };
            })
            .slice(0, -1);
    });

    private initializeItem(item: Grid.UnInitializedItem<ItemType>) {
        return GridUtil.initializeItem({
            item,
            findOverlap: this.findOverlap,
            overlap: this.overlap,
            itemInitialized: this.itemInitialized(),
        });
    }

    private computeOverlap(): Grid.OverlapBundle<ItemType, ProjectionData> {
        const projections = this.visibleItemProjections();
        const columnCount = this.columnHeadersMeta.count();
        const rowCount = this.rowHeadersMeta.count();
        const neighbors = GridUtil.overlappingProjectionsByKey(projections, columnCount, rowCount);
        return {
            segments: GridUtil.findOverlapSegments(projections, neighbors),
            byProjectionKey: GridUtil.overlappingItemsByProjectionKey(neighbors),
            byItemId: GridUtil.overlappingSourcesByItemId(projections, neighbors),
        };
    }

    /**
     * What renders must react to visibility: an entry's own `hidden` flag, whether it still occupies space once
     * clipped, and the header metadata, since a hidden row or column collapses the entries it contains to zero
     * size. Raw positions stay untracked because they change on every frame of a drag, which must not rebuild the
     * rendered set. `renderable` is derived from the clipped position, so it notifies only when an entry crosses
     * on or off the grid — relocating an item into a filtered row, as undo does, renders it.
     */
    private visibleSorted<Entry extends Grid.RenderEntry>(entries: readonly Entry[]): Entry[] {
        const visible = entries.filter((entry) => !entry.hidden() && (entry.renderable?.() ?? true));
        // Read for their dependencies only, so hiding or showing a row / column rebuilds the rendered set
        this.rowHeadersMeta.flatMeta();
        this.columnHeadersMeta.flatMeta();
        // Sorting reads positions, which change on every frame of a drag and must not rebuild the set
        return untracked(() => GridUtil.sortItems(visible, (entry) => entry.key));
    }

    private projectItems(
        items: readonly Grid.Item<ItemType>[],
        projector: Grid.ItemProjector<ItemType, ProjectionData>,
    ): Grid.ItemProjection<ItemType, ProjectionData>[] {
        return items.flatMap((item) => {
            return projector(item).map((definition) => this.createItemProjection(item, definition));
        });
    }

    /**
     * Two projection lists are interchangeable only when they describe the same items in the same
     * slots: matching keys *and* the same source objects. Keys alone are not enough — a parent that
     * re-creates its items produces projections with identical keys over new objects, and keeping
     * the older list would leave every tile rendering an item the grid no longer writes to, so
     * candidate geometry and move state would land on objects nothing is displaying.
     */
    private static projectionsEqual<Entry extends { key: string; source: Grid.SourceItem }>(
        first: readonly Entry[],
        second: readonly Entry[],
    ): boolean {
        if (first.length !== second.length) return false;
        return first.every((entry, index) => entry.key === second[index].key && entry.source === second[index].source);
    }

    isEntryInteractive(entry: GridRenderEntry<ItemType, ProjectionData, DecorationData>): boolean {
        if (entry.noInteraction()) return false;
        if (entry.type === "decoration") return entry.layer === "overlay";
        return true;
    }

    private createItemProjection(
        item: Grid.Item<ItemType>,
        definition: Grid.ItemProjectionDefinition<ProjectionData>,
    ): Grid.ItemProjection<ItemType, ProjectionData> {
        const clippedPosition = computed(() =>
            GridUtil.clipPosition(definition.position(), this.columnHeadersMeta, this.rowHeadersMeta),
        );
        const itemWindow = computed(() =>
            GridUtil.getItemWindow(
                definition.position(),
                clippedPosition(),
                this.columnHeadersMeta,
                this.rowHeadersMeta,
            ),
        );
        return {
            type: "item-projection",
            source: item,
            dragging: item._virtualGrid.isBeingDragged,
            moving: computed(() => !!item._virtualGrid.moving()),
            broughtToFront: item._virtualGrid.broughtToFront,
            overlappingItems: computed(() => this.overlap().byProjectionKey.get(definition.key) ?? []),
            ...definition,
            clippedPosition,
            window: itemWindow,
            renderable: computed(() => GridUtil.isOnGrid(clippedPosition())),
            frozenPosition: computed(() =>
                GridUtil.clipToFrozenRows(clippedPosition(), this.frozenRows(), this.rowHeadersMeta.visibleCount()),
            ),
        };
    }

    @Bind
    private checkForDuplicateIds() {
        if (!S25Const.isDevMode) return;
        const ids = new Set<ItemType["id"]>();
        for (const item of this.items()) {
            if (ids.has(item.id)) {
                console.error(`Virtual Grid: Duplicate item ID: ${item.id}`);
            } else {
                ids.add(item.id);
            }
        }
    }

    @Bind
    private checkForDuplicateRenderKeys() {
        if (!S25Const.isDevMode) return;
        const keys = new Set<Grid.RenderEntry["key"]>();
        for (const entry of this.renderEntries()) {
            if (keys.has(entry.key)) {
                console.error(`Virtual Grid: Duplicate render entry key: ${entry.key}`);
            } else {
                keys.add(entry.key);
            }
        }
    }

    private frozenLayerEntries(entries: readonly GridRenderEntry<ItemType, ProjectionData, DecorationData>[]) {
        if (this.frozenRows() <= 0 || this.rowHeadersMeta.visibleCount() <= 0) return [];
        return entries
            .filter((entry) => entry.frozenPosition())
            .map((entry) => ({ ...entry, clippedPosition: entry.frozenPosition as Signal<Grid.Rect>, frozen: true }));
    }

    /**
     * Handle the grid scrolling (horizontally).
     * Will scroll the fixed region if visible
     */
    @Bind
    onGridScroll() {
        const scrollArea = this.scrollArea();
        const fixedRegion = this.fixedRegion();
        if (!this.showFixedHeaders() || !scrollArea || !fixedRegion) return; // Only relevant when headers are sticky
        fixedRegion.nativeElement.scrollLeft = scrollArea.nativeElement.scrollLeft;
    }

    /**
     * Horizontal wheel over the fixed region does not reach the scroll area.
     * Forward it, and only consume the event when the grid actually moved.
     */
    @Bind
    onFixedWheel(event: WheelEvent) {
        const scrollArea = this.scrollArea()?.nativeElement;
        if (!scrollArea) return;
        const deltaX = event.shiftKey && event.deltaX === 0 ? event.deltaY : event.deltaX;
        if (!deltaX) return;
        const before = scrollArea.scrollLeft;
        scrollArea.scrollLeft = before + deltaX;
        if (scrollArea.scrollLeft !== before) event.preventDefault();
    }

    /**
     * Handle the document scrolling (vertically).
     * Updates "sticky" headers
     */
    @Bind
    onDocumentScroll() {
        this.updateStickyHeaders();
    }

    /**
     * Toggles the "sticky" headers on and off depending on the scroll position.
     */
    updateStickyHeaders() {
        const scrollArea = this.scrollArea();
        const fixedRegion = this.fixedRegion();
        if (!scrollArea || !fixedRegion) return;

        const { top, width, bottom } = scrollArea.nativeElement.getBoundingClientRect();

        // Make sure fixed headers don't go below the grid
        this.renderer.setStyle(
            fixedRegion.nativeElement,
            "--scroll-area-bottom",
            `${bottom}px`,
            RendererStyleFlags2.DashCase,
        );

        const shouldStick = top < 0;
        if (this.showFixedHeaders() === shouldStick) return; // Nothing changed
        this.showFixedHeaders.set(shouldStick);
        this.renderer.setStyle(fixedRegion.nativeElement, "display", shouldStick ? "block" : "none");
        this.renderer.setStyle(fixedRegion.nativeElement, "width", `${width}px`);

        if (shouldStick) this.onGridScroll(); // Set initial scroll position
    }

    resizeStickyHeaders() {
        const scrollArea = this.scrollArea();
        const fixedRegion = this.fixedRegion();
        if (!this.showFixedHeaders() || !scrollArea || !fixedRegion) return;

        const width = scrollArea.nativeElement.getBoundingClientRect().width;
        this.renderer.setStyle(fixedRegion.nativeElement, "width", `${width}px`);
    }

    /**
     * Handle window resizing.
     * Hides grid items for the duration of resizing
     */
    @Bind
    onWindowResize() {
        // Set class "resizing" on the "grid" element to hide items during resize for performance
        clearTimeout(this.resizeTimeout());
        this.resizeTimeout.set(
            setTimeout(() => {
                this.resizeTimeout.set(undefined);
            }, 100),
        );

        this.resizeStickyHeaders();
    }

    /**
     * Handle "enter" keypresses on items.
     * May pick up or put down item based on state
     * @param event Keyboard Event
     * @param elem Element being pressed
     * @param entry Grid entry being pressed
     */
    onItemEnter(event: Event, elem: HTMLElement, entry: Grid.ItemProjection<ItemType, ProjectionData>) {
        if (entry.type !== "item-projection" || !this.isEntryInteractive(entry)) return;
        if (event.target !== elem) return; // Only pick up when event is originating from the item (not bubbled)
        event.preventDefault(); // Prevent scrolling

        if (this.dragSession()?.type !== "keyboard") {
            return this.pickUpItem(entry.source, "keyboard", event.target as HTMLElement, entry.key);
        } else {
            return this.putDownItem();
        }
    }

    /**
     * Handle mouse down events
     * Picks up item if mouse button is left click
     * @param event Mouse event
     * @param entry Projection being clicked on
     */
    onItemMousedown(event: MouseEvent, entry: Grid.ItemProjection<ItemType, ProjectionData>) {
        if (entry.type !== "item-projection" || !this.isEntryInteractive(entry)) return;
        if (event.button !== 0) return;
        return this.pickUpItem(
            entry.source,
            "mouse",
            event.target as HTMLElement,
            entry.key,
            event.clientX,
            event.clientY,
        );
    }

    async pickUpItem(
        item: Grid.Item<ItemType>,
        type: Grid.DragSession<ItemType>["type"],
        elem: HTMLElement,
        grabbedProjectionKey: Grid.RenderEntry["key"] = this.renderKeyFromElement(elem) ?? item.id,
        clientX?: number,
        clientY?: number,
    ) {
        if (!this.canDrag()?.x && !this.canDrag()?.y) return;
        if (this.dragSession() || !item.draggable?.()) return;

        const participants = this.resolvePickupParticipants(item);
        const grabbedProjection = <Grid.ItemProjection<ItemType, ProjectionData>>(
            this.renderEntryByKey().get(grabbedProjectionKey)
        );
        if (!grabbedProjection || !this.isEntryDraggable(grabbedProjection)) return;

        for (const participant of participants) participant._virtualGrid.isBeingDragged.set(true);

        const dragOffset = signal<Grid.Coord | undefined>({ x: 0, y: 0 });
        for (const participant of participants) participant._virtualGrid.dragOffsetSource.set(dragOffset);

        const session: Grid.DragSession<ItemType, ProjectionData> = {
            type,
            participants,
            grabbedProjection,
            init: {
                pointerPercent:
                    clientX == null || clientY == null ? undefined : this.pointerToVisiblePercent(clientX, clientY),
                position: grabbedProjection.position(),
                clippedPosition: grabbedProjection.clippedPosition(),
            },
            offset: { x: 0, y: 0 },
            dragOffset,
        };
        this.dragSession.set(session);

        const [ok] = await S25Util.Maybe(
            this.interactionHandlers().onPickup?.({
                primary: item,
                participants,
                grabbedEntry: grabbedProjection,
            }) ?? true,
        );
        if (ok) AriaLive.announce("Picked up item", true);
        else {
            this.cancelDrag(session, false);
            AriaLive.announce("Item could not be picked up", true);
        }
    }

    private resolvePickupParticipants(primary: Grid.Item<ItemType>): Grid.Item<ItemType>[] {
        const extra = this.dragParticipantsResolver()?.(primary, this.items()) ?? [];
        const seen = new Set<Grid.SourceItem["id"]>([primary.id]);
        const participants: Grid.Item<ItemType>[] = [primary];
        for (const item of extra) {
            if (seen.has(item.id)) continue;
            seen.add(item.id);
            participants.push(item);
        }
        return participants;
    }

    private renderKeyFromElement(element: HTMLElement): string | undefined {
        const renderRoot = element.closest?.("[data-render-key]") as HTMLElement | null;
        return renderRoot?.dataset.renderKey ?? element.dataset.renderKey;
    }

    /**
     * Handles the mouse moving.
     * Will move item if currently dragging with mouse
     * @param event Mouse event
     */
    @Bind
    onDocumentMousemove(event: MouseEvent) {
        if (this.dragSession()?.type === "mouse") this.onItemMouseDrag(event);
        else if (this.resizingRowHeaders()) this.resizeRowHeaders(event);
        else if (this.canCreate()?.x || this.canCreate()?.y) this.onCreateMousemove(event);
    }

    /**
     * Moves an item using the keyboard.
     * @param event Keyboard event
     * @param dx STEPS to move item horizontally
     * @param dy STEPS to move item vertically
     */
    onItemKeyboardDrag(event: Event | undefined, dx: number, dy: number) {
        const gridArea = this.gridArea();
        const session = this.dragSession();
        if (session?.type !== "keyboard" || !gridArea || !this.canDrag()) return;
        event?.preventDefault?.();

        let stepX = 0;
        if (this.canDrag()?.x) {
            const gridWidth = gridArea.nativeElement.offsetWidth;
            stepX = Math.round((gridWidth / this.columnHeadersMeta.visibleCount()) * this.snapStep().x * dx);
            session.offset.x += (stepX / gridWidth) * 100;
        }

        let stepY = 0;
        if (this.canDrag()?.y) {
            const gridHeight = gridArea.nativeElement.offsetHeight;
            stepY = Math.round((gridHeight / this.rowHeadersMeta.visibleCount()) * this.snapStep().y * dy);
            session.offset.y += (stepY / gridHeight) * 100;
        }

        this.updateDragItems();
        this.scrollBy(stepX, stepY);
        this.notifyDragChanged();
    }

    /**
     * Moves an item with the mouse.
     * @param event Mousemove event
     */
    onItemMouseDrag(event: MouseEvent) {
        const session = this.dragSession();
        if (session?.type !== "mouse" || !this.canDrag()) return;

        const { offset, init } = session;
        const gridArea = this.gridArea()?.nativeElement;
        const pointer = init.pointerPercent ? this.pointerToVisiblePercent(event.clientX, event.clientY) : undefined;
        if (!pointer || !init.pointerPercent || !gridArea) return;

        if (this.canDrag()?.x) offset.x = pointer.x - init.pointerPercent.x;
        if (this.canDrag()?.y) offset.y = pointer.y - init.pointerPercent.y;

        this.autoScroll(event);
        this.scheduleMouseDragFrame();
    }

    private scheduleMouseDragFrame() {
        if (this.dragFrame !== undefined) return;
        this.dragFrame = requestAnimationFrame(this.applyPendingMouseDrag);
    }

    @Bind
    private applyPendingMouseDrag() {
        this.dragFrame = undefined;
        this.updateDragItems();
        this.notifyDragChanged();
    }

    private cancelPendingMouseDragFrame() {
        if (this.dragFrame === undefined) return;
        cancelAnimationFrame(this.dragFrame);
        this.dragFrame = undefined;
    }

    /**
     * Calculates candidate positions for the current drag session.
     * Accounts for snapping to the grid and hidden columns and rows.
     */
    updateDragItems() {
        const gridArea = this.gridArea();
        const session = this.dragSession();
        if (!gridArea || !session || !this.canDrag()) return;

        const offset = GridDrag.getFullGridOffset({
            ...session,
            resolution: this.snapStep(),
            columnHeadersMeta: this.columnHeadersMeta,
            rowHeadersMeta: this.rowHeadersMeta,
            gridHeight: gridArea.nativeElement.offsetHeight,
            gridWidth: gridArea.nativeElement.offsetWidth,
        });
        session.dragOffset.set(offset);
    }

    /**
     * Visible-grid percent under the pointer. Y uses the frozen pane when the pointer is in that band,
     * otherwise the main grid, whose rect already reflects how far the document has scrolled.
     */
    private pointerToVisiblePercent(clientX: number, clientY: number): Grid.Coord | undefined {
        const scrollArea = this.scrollArea()?.nativeElement;
        const gridArea = this.gridArea()?.nativeElement;
        if (!scrollArea || !gridArea) return;
        const gridWidth = gridArea.offsetWidth;
        const gridHeight = gridArea.offsetHeight;
        if (!gridWidth || !gridHeight) return;

        const scrollRect = scrollArea.getBoundingClientRect();
        const x = ((clientX - scrollRect.left + scrollArea.scrollLeft) / gridWidth) * 100;

        const visibleRows = this.rowHeadersMeta.visibleCount();
        const frozenRows = this.frozenRows();
        const paneRect = this.frozenRowsPane()?.nativeElement.getBoundingClientRect();
        if (
            paneRect?.height &&
            frozenRows > 0 &&
            visibleRows > 0 &&
            clientY >= paneRect.top &&
            clientY <= paneRect.bottom
        ) {
            const fraction = (clientY - paneRect.top) / paneRect.height;
            return { x, y: fraction * (frozenRows / visibleRows) * 100 };
        }

        const gridRect = gridArea.getBoundingClientRect();
        const y = ((clientY - gridRect.top) / gridHeight) * 100;
        return { x, y };
    }

    /** Visible intersection of the frozen canvas and the fixed region that clips it. */
    private pointerOverFrozenRows(clientX: number, clientY: number) {
        const pane = this.frozenRowsPane()?.nativeElement;
        const fixed = this.fixedRegion()?.nativeElement;
        if (!pane || !fixed || !this.showFixedHeaders() || this.frozenRows() <= 0) return false;
        const paneRect = pane.getBoundingClientRect();
        const fixedRect = fixed.getBoundingClientRect();
        if (paneRect.height <= 0 || fixedRect.width <= 0) return false;
        const left = Math.max(paneRect.left, fixedRect.left);
        const right = Math.min(paneRect.right, fixedRect.right);
        const top = Math.max(paneRect.top, fixedRect.top);
        const bottom = Math.min(paneRect.bottom, fixedRect.bottom);
        return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
    }

    private notifyDragChanged() {
        const session = this.dragSession();
        if (!session) return;
        this.interactionHandlers().onDragChanged?.({
            primary: session.grabbedProjection!.source,
            participants: session.participants,
        });
    }

    /**
     * Automatically scrolls the grid based on the position of the mouse.
     * @param event Mouse event
     */
    autoScroll(event: MouseEvent) {
        const scrollArea = this.scrollArea();
        if (!scrollArea) return;

        const { AUTO_SCROLL_THRESHOLD, AUTO_SCROLL_STEP } = S25VirtualGridComponent;
        const { x, width } = scrollArea.nativeElement.getBoundingClientRect();

        const posX = S25Util.clamp((event.clientX - x) / width, 0.01, 1);
        const posY = S25Util.clamp(event.clientY / window.innerHeight, 0.01, 1);

        const scrollX = this.canDrag()?.x ? this.getScrollValue(posX, AUTO_SCROLL_THRESHOLD, AUTO_SCROLL_STEP) : 0;
        // The frozen pane is pinned to the top of the viewport, so that Y is not a document-scroll edge.
        const yEnabled = this.canDrag()?.y && !this.pointerOverFrozenRows(event.clientX, event.clientY);
        const scrollY = yEnabled ? this.getScrollValue(posY, AUTO_SCROLL_THRESHOLD, AUTO_SCROLL_STEP) : 0;

        this.scrollBy(scrollX, scrollY);
    }

    /**
     * Calculates the amount to scroll the grid by, based on the proximity to the edge
     * @param offset Position in element. Typically 0-1
     * @param threshold The distance from the edge at which to start scrolling. 0-1
     * @param step The size of each scroll step in pixels
     */
    getScrollValue(offset: Proto.Fraction, threshold: number, step: number) {
        if (offset > threshold && offset < 1 - threshold) return 0; // Between bounds

        const direction = offset <= threshold ? -1 : 1;
        const overlap = (direction < 0 ? offset : 1 - offset) / threshold;
        const scale = (1 - overlap) / overlap + 1; // Scroll quicker the closer we are to the edge
        return direction * step * scale;
    }

    /**
     * Scrolls the grid by x and y pixels
     * @param x Pixels to scroll by
     * @param y Pixels to scroll by
     */
    scrollBy(x: number, y: number) {
        const scrollArea = this.scrollArea();
        if (!scrollArea) return;

        // Make sure we don't scroll past the grid
        const { limit } = S25ScrollUtil.getScrollLimit(
            scrollArea.nativeElement,
            document.documentElement,
            scrollArea.nativeElement,
        );

        // Vertically
        const scrollTop = document.documentElement.scrollTop;
        if (y < 0 && scrollTop + y < limit.top.min) {
            y = S25Util.clamp(y, limit.top.min - scrollTop, 0);
        } else if (y > 0 && scrollTop + y > limit.top.max) {
            y = S25Util.clamp(y, 0, limit.top.max - scrollTop);
        }

        // Horizontally
        const scrollLeft = scrollArea.nativeElement.scrollLeft;
        if (x < 0 && scrollLeft + x < limit.left.min) {
            x = S25Util.clamp(x, limit.left.min - scrollLeft, 0);
        } else if (x > 0 && scrollLeft + x > limit.left.max) {
            x = S25Util.clamp(x, 0, limit.left.max - scrollLeft);
        }

        scrollArea.nativeElement.scrollBy({ left: x });
        this.document.documentElement.scrollBy({ top: y });
    }

    /**
     * Handles mouseup events on the document.
     * Will stop dragging items if we are currently dragging by mouse
     * @param event Mouse event
     * @HostListener document:mouseup
     */
    @HostListener("document:mouseup", ["$event"])
    onDocumentMouseup(event: MouseEvent) {
        if (this.dragSession()?.type === "mouse") this.onItemMousePutDown();
        if (this.creating()?.dragging) this.onCreateMouseup();
        this.resizingRowHeaders.set(undefined);
    }

    /**
     * Puts down an item being dragged via keyboard.
     */
    onItemKeyboardPutDown() {
        if (this.dragSession()?.type !== "keyboard") return;
        return this.putDownItem();
    }

    /**
     * Puts down an item being dragged via mouse.
     */
    onItemMousePutDown() {
        if (this.dragSession()?.type !== "mouse") return;
        return this.putDownItem();
    }

    /**
     * Puts down an item being dragged.
     */
    async putDownItem() {
        const session = this.dragSession();
        if (!session || !this.canDrag()) return;

        this.cancelPendingMouseDragFrame();
        this.updateDragItems();
        const dropContext: Grid.DropContext<ItemType> = {
            primary: session.grabbedProjection!.source,
            participants: session.participants,
        };

        this.blockDragging.set(true);
        const [dropResult, err] = await S25Util.Maybe(this.interactionHandlers().onDrop?.(dropContext) ?? true);
        this.blockDragging.set(false);
        if (!dropResult || err) {
            this.cancelDrag();
            AriaLive.announce("Item could not be put down and was return to its original position", true);
            return;
        }

        // A handler can report extra participants it discovered mid-drop (e.g. a swap partner). Fold
        // them in here so onCommit and the undo/redo history both see every item the drop touched.
        const commitContext: Grid.CommitContext<ItemType> =
            typeof dropResult === "object"
                ? { ...dropContext, participants: [...session.participants, ...dropResult.participants] }
                : dropContext;

        const from = this.readHistoryPositions(commitContext.participants);
        this.interactionHandlers().onCommit?.(commitContext);
        const to = this.readHistoryPositions(commitContext.participants);

        this.clearDragState(session);
        this.dragSession.set(undefined);
        AriaLive.announce("Item was put down", true);

        this.history.change({
            data: {
                primaryId: session.grabbedProjection!.source.id,
                itemIds: commitContext.participants.map((participant) => participant.id),
            },
            from,
            to,
        });
    }

    private readHistoryPositions(items: readonly Grid.Item<ItemType>[]) {
        return new Map(items.map((item) => [item.id, item.position() as Grid.PositionOf<ItemType>]));
    }

    private clearDragState(session: Grid.DragSession<ItemType, ProjectionData>) {
        session.dragOffset.set(undefined);
        const idle = signal<Grid.Coord | undefined>(undefined);
        for (const participant of session.participants) {
            participant._virtualGrid.dragOffsetSource.set(idle);
            participant._virtualGrid.isBeingDragged.set(false);
        }
    }

    /**
     * Clears candidate positions without touching committed geometry.
     */
    cancelDrag(session = this.dragSession(), notifyInteraction = true) {
        if (!session) return;
        this.cancelPendingMouseDragFrame();
        this.clearDragState(session);
        this.dragSession.set(undefined);
        if (notifyInteraction) {
            this.interactionHandlers().onCancel?.({
                primary: session.grabbedProjection!.source,
                participants: session.participants,
            });
        }
    }

    /**
     * Starts resizing the headers
     * @param event Mouse event
     */
    onRowHeaderResizeMousedown(event: MouseEvent) {
        const rowHeaders = this.rowHeaderElems();
        if (!rowHeaders) return;

        const initWidth = parseFloat(this.getCSSProperty("width", rowHeaders.nativeElement));
        this.resizingRowHeaders.set({ initX: event.clientX, initWidth });
    }

    /**
     * Resizes the headers
     * @param event Mouse event
     */
    resizeRowHeaders(event: MouseEvent) {
        const resizing = this.resizingRowHeaders();
        if (!resizing) return;
        const offset = event.clientX - resizing.initX;
        const newWidth = Math.max(resizing.initWidth + offset, 10); // Minimum 10px

        this.elementRef.nativeElement.style.setProperty("--column-width-first", `${newWidth}px`); // Cannot be done with renderer.setStyle()
        this.resizeStickyHeaders();
    }

    /**
     * Gets a computed CSS property
     * @param prop CSS property
     * @param [elem=Host] Element to get the property from
     */
    getCSSProperty(prop: string, elem = this.elementRef.nativeElement) {
        return getComputedStyle(elem).getPropertyValue(prop);
    }

    /**
     * Sets up event necessary event listeners
     */
    @Bind
    setEventListeners(onCleanup: EffectCleanupRegisterFn) {
        const scrollArea = this.scrollArea()?.nativeElement;
        const fixedRegion = this.fixedRegion()?.nativeElement;
        if (!scrollArea || !fixedRegion) return;
        const listeners: ReturnType<Renderer2["listen"]>[] = [];
        this.zone.runOutsideAngular(() => {
            listeners.push(this.renderer.listen(document, "mousemove", this.onDocumentMousemove));
            listeners.push(this.renderer.listen(window, "resize", this.onWindowResize));
            listeners.push(this.renderer.listen(document, "scroll", this.onDocumentScroll));
            listeners.push(this.renderer.listen(scrollArea, "scroll", this.onGridScroll));
            listeners.push(this.renderer.listen(fixedRegion, "wheel", this.onFixedWheel));
            listeners.push(this.renderer.listen(document, "keydown.control.z", this.undo));
            listeners.push(this.renderer.listen(document, "keydown.control.shift.z", this.redo));
            listeners.push(this.renderer.listen(document, "keydown.control.y", this.redo));
            // Meta is the mac CMD key
            listeners.push(this.renderer.listen(document, "keydown.meta.z", this.undo));
            listeners.push(this.renderer.listen(document, "keydown.meta.shift.z", this.redo));
            listeners.push(this.renderer.listen(document, "keydown.meta.y", this.redo));
        });
        onCleanup(() => {
            this.cancelPendingMouseDragFrame();
            for (const listener of listeners) listener();
        });
    }

    @Bind
    undo() {
        this.undoOrRedo("undo");
    }

    @Bind
    redo() {
        this.undoOrRedo("redo");
    }

    @Bind
    async undoOrRedo(type: Grid.HistoryDirection) {
        const confirmationModal = this.confirmationModal();
        if (!this.hasUndo() || this.isUnOrRedoing() || !confirmationModal) return;

        const change = this.history[type]();
        if (!change) return;

        const itemsById = this.itemById();
        const participants = change.data.itemIds.map((itemId) => itemsById.get(itemId)).filter(S25Util.isDefined);
        const primary = itemsById.get(change.data.primaryId) ?? participants[0];
        if (!primary) {
            this.history[type === "undo" ? "redo" : "undo"]();
            return;
        }

        const source = type === "undo" ? change.to : change.from;
        const domainEquals = this.domainEquals();
        const superseded = [...source].some(([itemId, position]) => {
            const item = itemsById.get(itemId);
            return !item || !domainEquals(item.position() as Grid.PositionOf<ItemType>, position);
        });
        if (superseded) {
            const message = `The action you are attempting to ${type} has been superseded by another. Do you wish to continue ${type}ing?`;
            this.confirmationText.set(message);
            const ok = await confirmationModal.open();
            if (!ok) {
                this.history[type === "undo" ? "redo" : "undo"]();
                return;
            }
        }

        const historyContext: Grid.HistoryContext<ItemType> = {
            primary,
            participants,
            from: change.from,
            to: change.to,
        };
        const destination = type === "undo" ? change.from : change.to;
        for (const participant of participants) {
            participant._virtualGrid.candidatePosition.set(destination.get(participant.id));
        }
        this.isUnOrRedoing.set(true);
        try {
            const move = this.startMove(participants);
            const service = type === "undo" ? this.interactionHandlers().onUndo : this.interactionHandlers().onRedo;
            const [ok, err] = await S25Util.Maybe(service?.(historyContext) ?? true);
            if (!ok || err) {
                void this.startMove(participants);
                if (err) alert(`Action could not be ${type}ne`);
                this.history[type === "undo" ? "redo" : "undo"]();
                return;
            }

            // Committing can re-key a projection, which replaces the tile element, so let the move land first
            await move;
            this.interactionHandlers().onCommit?.({
                primary,
                participants,
            });
        } finally {
            for (const participant of participants) participant._virtualGrid.candidatePosition.set(undefined);
            this.isUnOrRedoing.set(false);
        }
    }

    /**
     * Moves an item to `destination`, animating its tile from wherever it is standing. Changes that
     * never went through a drag, like a peer's move arriving on a poll, have no drag session to
     * animate out of, so they come through here instead of writing committed geometry themselves.
     */
    async moveItem(item: Grid.Item<ItemType>) {
        return this.startMove([item]);
    }

    /**
     * Opens the animation window for a set of items: their tiles animate themselves from wherever
     * they are standing to whatever geometry is written while the window is open.
     */
    private async startMove(participants: readonly Grid.Item<ItemType>[]) {
        const startTime = Date.now();
        for (const participant of participants) participant._virtualGrid.moving.set({ startTime });

        await S25Util.delay(S25VirtualGridComponent.ITEM_MOVE_DURATION);
        for (const participant of participants) {
            if (participant._virtualGrid.moving()?.startTime !== startTime) continue;
            participant._virtualGrid.moving.set(undefined);
        }
    }

    onCreateMousemove(event: MouseEvent) {
        const createItem = this.createItem();
        const creating = this.creating();
        if (!this.createTemplate() || !createItem) return;
        if (creating?.freeze) return;

        const gridArea = this.gridArea()?.nativeElement;
        if (!gridArea) return;

        const rect = gridArea.getBoundingClientRect();
        const x = S25Util.clamp(event.clientX - rect.left, 0, gridArea.offsetWidth);
        const y = S25Util.clamp(event.clientY - rect.top, 0, gridArea.offsetHeight);

        if (creating?.dragging) {
            this.onCreateDrag({ x, y });
        } else if (event.target === gridArea || event.target === createItem.nativeElement) {
            this.showCreateItem({ x, y });
        }
    }

    async showCreateItem(offset: { x: number; y: number }) {
        const gridArea = this.gridArea()?.nativeElement;
        if (this.creating()?.dragging || !gridArea) return;

        const columnWidth = gridArea.offsetWidth / this.columnHeadersMeta.visibleCount();
        const startX = Math.floor(offset.x / columnWidth / this.snapStep().x) * columnWidth * this.snapStep().x;
        const rowHeight = gridArea.offsetHeight / this.rowHeadersMeta.visibleCount();
        const startY = Math.floor(offset.y / rowHeight / this.snapStep().y) * rowHeight * this.snapStep().y;
        this.creating.set({
            start: { x: startX, y: startY },
            end: { x: startX + columnWidth, y: startY + rowHeight },
        });

        // Before showing, validate with API that we can create here
        const position = this.getCreatePosition()!;
        const [ok, err] = await S25Util.Maybe(this.interactionHandlers().onCreateShow?.(position) ?? true);
        this.updateCreatePosition(ok);
    }

    onCreateMousedown(event: MouseEvent) {
        const gridArea = this.gridArea()?.nativeElement;
        if (!gridArea) return;
        if (event.button !== 0) return; // Main button
        const creating = this.creating();
        if (!creating || creating.freeze) return;
        if (!this.canCreate()?.x && !this.canCreate()?.y) return;

        this.creating.set({ ...creating, dragging: true });
        this.renderer.addClass(gridArea, "creating");
    }

    async onCreateMouseup() {
        const gridArea = this.gridArea()?.nativeElement;
        if (!gridArea) return;
        const creating = this.creating();
        if (!creating || creating.freeze) return;
        const position = this.getCreatePosition()!;
        this.creating.set({ ...creating, freeze: true });
        await S25Util.Maybe(this.interactionHandlers().onCreate?.(position));
        this.updateCreatePosition(false);
    }

    async onCreateDrag(offset: { x: number; y: number }) {
        const gridArea = this.gridArea()?.nativeElement;
        const creating = this.creating();
        if (!creating?.dragging || !gridArea) return;

        const prev = { ...creating };
        const end = { ...creating.end };

        if (this.canCreate()?.y) {
            const rowCount = this.rowHeadersMeta.visibleCount();
            const rowHeight = gridArea.offsetHeight / rowCount;
            const rows = Math.min(offset.y / rowHeight + (offset.y >= prev.start.y ? 1 : 0), rowCount);
            end.y = Math.floor(rows / this.snapStep().y) * rowHeight * this.snapStep().y;
        }
        if (this.canCreate()?.x) {
            const colCount = this.columnHeadersMeta.visibleCount();
            const columnWidth = gridArea.offsetWidth / colCount;
            const cols = Math.min(offset.x / columnWidth + (offset.x >= prev.start.x ? 1 : 0), colCount);
            end.x = Math.floor(cols / this.snapStep().x) * columnWidth * this.snapStep().x;
        }
        this.creating.set({ ...creating, end });

        // Check if allowed to continue dragging into this spot
        const position = this.getCreatePosition()!;
        const [ok, err] = await S25Util.Maybe(this.interactionHandlers().onCreateDrag?.(position) ?? true);
        if (!ok || err) {
            this.creating.set(prev);
        }

        this.updateCreatePosition(true);
    }

    private getCreatePosition() {
        const gridArea = this.gridArea()?.nativeElement;
        const creating = this.creating();
        if (!gridArea || !creating) return;
        return GridUtil.getCreatePosition({
            creating,
            gridArea,
            columnMeta: this.columnHeadersMeta,
            rowMeta: this.rowHeadersMeta,
        });
    }

    updateCreatePosition(show: boolean) {
        const gridArea = this.gridArea()?.nativeElement;
        const createItem = this.createItem()?.nativeElement;
        if (!gridArea || !createItem) return;

        if (!show) {
            this.renderer.removeClass(createItem, "can-create");
            this.renderer.removeClass(gridArea, "creating");
            this.creating.set(undefined);
            return;
        }

        const columnVisibleCount = this.columnHeadersMeta.visibleCount();
        const rowVisibleCount = this.rowHeadersMeta.visibleCount();
        const columnWidth = gridArea.offsetWidth / columnVisibleCount;
        const rowHeight = gridArea.offsetHeight / rowVisibleCount;

        const { start, end } = this.creating()!;
        const originX = end.x < start.x ? start.x + columnWidth : start.x;
        const originY = end.y < start.y ? start.y + rowHeight : start.y;

        const left = Math.min(originX, end.x);
        const width = Math.abs(originX - end.x);
        const top = Math.min(originY, end.y);
        const height = Math.abs(originY - end.y);

        this.renderer.setStyle(createItem, "--item-left", `${left}px`, RendererStyleFlags2.DashCase);
        this.renderer.setStyle(createItem, "--item-top", `${top}px`, RendererStyleFlags2.DashCase);
        this.renderer.setStyle(createItem, "--item-width", `${width}px`, RendererStyleFlags2.DashCase);
        this.renderer.setStyle(createItem, "--item-height", `${height}px`, RendererStyleFlags2.DashCase);
        this.renderer.addClass(createItem, "can-create");
    }

    positionToHeaders(position: Grid.Rect) {
        const columns = GridUtil.positionToHeaders(
            position.left,
            position.width,
            this.snapStep().x,
            this.columnHeadersMeta.flatMeta(),
        );
        const rows = GridUtil.positionToHeaders(
            position.top,
            position.height,
            this.snapStep().y,
            this.rowHeadersMeta.flatMeta(),
        );

        return { columns, rows };
    }

    /**
     * Brings the items to the front using z-index. Returns any item currently brought to front
     * @param toFront The item to bring to the front
     */
    bringToFront(toFront?: ItemType): void {
        for (const item of this.items()) item._virtualGrid.broughtToFront.set(item === toFront);
    }

    clearHistory() {
        this.history.clear();
    }

    /**
     * Renders every virtual item regardless of viewport size (for testing purposes)
     */
    renderAll() {
        for (const gridDirective of this.gridDirectives()) gridDirective.renderAll();
    }

    // Draws a representation of the grid on a canvas in the minimap
    @Bind
    drawMinimap() {
        if (this.dragSession()) return;
        const canvas = this.minimapCanvas();
        const columnVisibleCount = this.columnHeadersMeta.visibleCount();
        const rowVisibleCount = this.rowHeadersMeta.visibleCount();
        if (!canvas || !rowVisibleCount || !columnVisibleCount) return;
        const context: CanvasRenderingContext2D = canvas.nativeElement.getContext("2d")!;
        if (!context) return;

        // One pixel per snap step
        const canvasElementHeight = canvas.nativeElement.offsetHeight;
        const canvasElementWidth = canvas.nativeElement.offsetWidth;
        let canvasHeight = rowVisibleCount / this.snapStep().y;
        let canvasWidth = columnVisibleCount / this.snapStep().x;

        // Ensure that we scale down (when necessary) rather than up to avoid blur
        while (canvasHeight < canvasElementHeight) canvasHeight += rowVisibleCount;
        while (canvasWidth < canvasElementWidth) canvasWidth += columnVisibleCount;

        context.canvas.height = canvasHeight;
        context.canvas.width = canvasWidth;

        context.clearRect(0, 0, canvasWidth, canvasHeight);
        const defaultFill = "gray";
        const minimapItems = this.minimapRenderEntries().map((entry) => ({
            color: entry.minimapColor() || defaultFill,
            left: Math.round((entry.clippedPosition().left / 100) * canvasWidth),
            top: Math.round((entry.clippedPosition().top / 100) * canvasHeight),
            width: Math.round((entry.clippedPosition().width / 100) * canvasWidth),
            height: Math.round((entry.clippedPosition().height / 100) * canvasHeight),
        }));
        this.interactionHandlers()?.beforeMinimapRender?.(context, minimapItems);
        for (const item of minimapItems) {
            context.fillStyle = item.color || defaultFill;
            context.fillRect(item.left, item.top, item.width, item.height);
        }
        this.interactionHandlers()?.afterMinimapRender?.(context);
    }

    @Bind
    hasItem(item: ItemType) {
        return this.itemById().has(item.id);
    }

    isEntryDraggable(entry: Grid.RenderEntry<ItemType>) {
        if (entry.type === "decoration") return false;
        if (!this.isEntryInteractive(entry)) return false;
        if (!this.canDrag()?.x && !this.canDrag()?.y) return false;
        if (entry.draggable?.() === false) return false;
        if (entry.type === "item-projection" && !entry.source.draggable?.()) return false;
        return true;
    }

    onEntryFocused(entry: Grid.RenderEntry<ItemType>) {
        if (entry.type !== "item-projection" || !this.isEntryDraggable(entry)) return;
        AriaLive.announce("Press Enter or Space to grab");
    }
}
